import type { ContractType } from '@/types/commercial/contracts';
import { CONTRACT_SUMMARY_SCHEMAS } from '../contract-summary-schema';

export interface SummaryRow {
  label: string;
  value: string;
}

export interface ClauseSection {
  title: string;
  paragraphs: string[];
}

export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_\-.\s]+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatSummaryValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? '—' : trimmed;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map(formatSummaryValue).join(', ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '—';
    return entries.map(([k, v]) => `${humanizeKey(k)}: ${formatSummaryValue(v)}`).join('; ');
  }
  return '—';
}

export function buildSummaryRows(
  type: ContractType,
  summary: Record<string, unknown>,
): SummaryRow[] {
  const schema = CONTRACT_SUMMARY_SCHEMAS[type];
  if (schema) {
    const known = new Set(schema.fields.map((field) => field.key));
    const rows = schema.fields
      .map((field) => ({ label: field.label, value: formatSummaryValue(summary[field.key]) }))
      .filter((row) => row.value !== '—');
    const extras = Object.entries(summary)
      .filter(([key]) => !known.has(key))
      .map(([key, value]) => ({ label: humanizeKey(key), value: formatSummaryValue(value) }))
      .filter((row) => row.value !== '—');
    return [...rows, ...extras];
  }
  return Object.entries(summary)
    .map(([key, value]) => ({ label: humanizeKey(key), value: formatSummaryValue(value) }))
    .filter((row) => row.value !== '—');
}

export function buildClauseSections(clauses: Record<string, unknown>[]): ClauseSection[] {
  return clauses.map((clause, index) => {
    const title =
      typeof clause.title === 'string' && clause.title.trim() !== ''
        ? clause.title.trim()
        : `Cláusula ${index + 1}`;
    const fallback = Object.entries(clause)
      .filter(([key]) => key !== 'title' && key !== 'body')
      .map(([key, value]) => `${humanizeKey(key)}: ${formatSummaryValue(value)}`);
    const paragraphs =
      typeof clause.body === 'string' && clause.body.trim() !== ''
        ? clause.body
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
        : fallback;
    return { title, paragraphs };
  });
}
